import { useEffect, useId, useRef, type ReactNode, type RefObject } from 'react'
import { Glass } from '@/components/ui/glass'
import { cn } from '@/lib/utils'

interface DialogProps {
  open: boolean
  onClose: () => void
  /** Accessible name, used when the dialog has no heading. */
  label: string
  /** Element to focus after closing (defaults to the browser's own restore). */
  returnFocusRef?: RefObject<HTMLElement | null>
  variant?: 'modal' | 'sheet'
  /** Sheet edge; right for detail panels, left for the navigation drawer. */
  side?: 'left' | 'right'
  className?: string
  /** Overrides the sheet body's padding/height (the nav drawer fills it edge to edge). */
  contentClassName?: string
  children: ReactNode
}

let bodyLocks = 0

const FOCUSABLE ='a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

function focusables(root: HTMLElement): HTMLElement[] {
  return [...root.querySelectorAll<HTMLElement>(FOCUSABLE)].filter((el) => el.tabIndex >= 0 || el.getAttribute('tabindex') !== '-1')
}

/**
 * Native <dialog> + showModal(). Esc closes only the topmost dialog, focus starts on the heading,
 * and Tab stays inside. The inner div carries the padding so a click on the backdrop closes it.
 */
export function Dialog({ open, onClose, label, returnFocusRef, variant = 'modal', side = 'right', className, contentClassName, children }: DialogProps) {
  const ref = useRef<HTMLDialogElement>(null)
  const openRef = useRef(open)
  openRef.current = open
  const headingId = useId()

  useEffect(() => {
    const d = ref.current
    if (!d) return
    if (open && !d.open) d.showModal()
    if (!open && d.open) d.close()
    if (!open) return
    const heading = d.querySelector('h1, h2, h3')
    // A field marked data-autofocus (the reason box in ConfirmDialog) wins; the heading still names the dialog.
    const auto = d.querySelector<HTMLElement>('[data-autofocus]')
    if (heading instanceof HTMLElement) {
      if (!heading.id) heading.id = headingId
      heading.tabIndex = -1
      d.setAttribute('aria-labelledby', heading.id)
      ;(auto ?? heading).focus()
      return
    }
    const first = focusables(d)[0]
    ;(first ?? d).focus()
  }, [open, headingId])

  // The page must not scroll under the backdrop. Counted, not saved-and-restored: a sheet and its confirm
  // dialog can close in one render, and React runs their cleanups in tree order, not open order.
  useEffect(() => {
    if (!open) return
    if (bodyLocks++ === 0) document.body.style.overflow = 'hidden'
    return () => {
      if (--bodyLocks === 0) document.body.style.overflow = ''
    }
  }, [open])

  return (
    <dialog
      ref={ref}
      aria-modal="true"
      aria-label={label}
      onCancel={(e) => {
        const openDialogs = [...document.querySelectorAll('dialog')].filter((el) => (el as HTMLDialogElement).open)
        if (openDialogs[openDialogs.length - 1] !== ref.current) e.preventDefault()
      }}
      onKeyDown={(e) => {
        if (e.key !== 'Tab') return
        const d = ref.current
        if (!d) return
        const items = focusables(d)
        if (!items.length) {
          e.preventDefault()
          return
        }
        const first = items[0]
        const last = items[items.length - 1]
        const active = document.activeElement
        if (e.shiftKey && (active === first || active === d)) {
          e.preventDefault()
          last.focus()
        } else if (!e.shiftKey && active === last) {
          e.preventDefault()
          first.focus()
        }
      }}
      onClose={() => {
        returnFocusRef?.current?.focus()
        if (openRef.current) onClose() // Esc / backdrop; not when closed from code
      }}
      onClick={(e) => {
        // Backdrop only. A short sheet's own empty space is also the <dialog>, so check the box, not the target.
        if (e.target !== e.currentTarget || e.detail === 0) return
        const r = e.currentTarget.getBoundingClientRect()
        if (e.clientX < r.left || e.clientX > r.right || e.clientY < r.top || e.clientY > r.bottom) onClose()
      }}
      className={cn(
        'p-0 text-foreground',
        variant === 'sheet'
          ? cn(
              'fixed inset-y-0 m-0 h-dvh max-h-dvh w-full max-w-none rounded-none bg-card shadow-xl backdrop:bg-black/30 sm:w-[420px]',
              side === 'left' ? 'right-auto left-0 border-r border-border' : 'right-0 left-auto border-l border-border',
            )
          : 'm-auto w-[min(32rem,calc(100vw-2rem))] overflow-visible border-0 bg-transparent backdrop:bg-[rgb(14_24_56/0.22)] dark:backdrop:bg-black/40',
        className,
      )}
    >
      {open ? (
        variant === 'sheet' ? (
          <div className={cn('max-h-dvh overflow-y-auto p-5', contentClassName)}>{children}</div>
        ) : (
          <Glass radius={20}>
            <div className="max-h-[calc(100dvh-2rem)] overflow-y-auto p-6">{children}</div>
          </Glass>
        )
      ) : null}
    </dialog>
  )
}

export function Sheet(props: Omit<DialogProps, 'variant'>) {
  return <Dialog {...props} variant="sheet" />
}
